import React, { useEffect, useRef, useState } from "react";

import {
  confirmPasswordReset,
  requestPasswordResetCode,
  verifyPasswordResetCode,
} from "./authenticationApi";
import {
  AUTH_COPY,
  AUTH_ERROR,
  MAXIMUM_PASSWORD_LENGTH,
  MINIMUM_PASSWORD_LENGTH,
} from "./authenticationConstants";
import "./treatmentAuthentication.css";

const STAGE = Object.freeze({
  request: "request",
  code: "code",
  password: "password",
});

const CODE_PATTERN = /^\d{6}$/;
const COPY = AUTH_COPY.passwordReset;

const messageFor = (error, fallback) => {
  if (error?.code === AUTH_ERROR.passwordResetCodeInvalid) {
    return AUTH_COPY.errors.resetCodeInvalid;
  }
  if (error?.code === AUTH_ERROR.rateLimited && error.userMessage) {
    return error.userMessage;
  }
  return fallback;
};

/**
 * WT-102: reset a forgotten password without leaving the intake.
 *
 * Email -> 6-digit code -> new password. On success the parent signs the
 * patient in with the new password, so the visit continues the same way as
 * after a normal login.
 */
export function PasswordResetStep({ email, apiBaseUrl, onComplete, onBack }) {
  const [stage, setStage] = useState(STAGE.request);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [resendIn, setResendIn] = useState(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // One request at a time: a double submit must not send two codes.
  const run = async (operation) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      await operation();
    } finally {
      inFlightRef.current = false;
      setPending(false);
    }
  };

  const sendCode = (isResend) => run(async () => {
    try {
      const { resendAvailableIn } = await requestPasswordResetCode(email, { apiBaseUrl });
      setResendIn(resendAvailableIn);
      setCode("");
      setStage(STAGE.code);
      if (isResend) setNotice(COPY.codeStep.resent);
    } catch (requestError) {
      setError(messageFor(requestError, AUTH_COPY.errors.resetCodeUnavailable));
    }
  });

  const handleRequest = (event) => {
    event.preventDefault();
    sendCode(false);
  };

  const handleVerify = (event) => {
    event.preventDefault();
    if (!CODE_PATTERN.test(code)) {
      setError(AUTH_COPY.errors.resetCodeRequired);
      return;
    }
    run(async () => {
      try {
        await verifyPasswordResetCode({ email, code }, { apiBaseUrl });
        setStage(STAGE.password);
      } catch (verifyError) {
        setError(messageFor(verifyError, AUTH_COPY.errors.resetUnavailable));
      }
    });
  };

  const handleConfirm = (event) => {
    event.preventDefault();
    if (newPassword.length < MINIMUM_PASSWORD_LENGTH) {
      setError(AUTH_COPY.errors.newPasswordTooShort);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(AUTH_COPY.errors.newPasswordMismatch);
      return;
    }
    run(async () => {
      try {
        await confirmPasswordReset({ email, code, newPassword }, { apiBaseUrl });
      } catch (confirmError) {
        if (confirmError?.code === AUTH_ERROR.passwordResetCodeInvalid) {
          // The code expired or was used up while the patient chose a password.
          setCode("");
          setStage(STAGE.code);
        }
        setError(
          confirmError?.fieldErrors?.new_password
            || messageFor(confirmError, AUTH_COPY.errors.resetUnavailable),
        );
        return;
      }
      onComplete({ email, password: newPassword });
    });
  };

  const errorMessage = error && (
    <p className="treatment-auth__error" id="intake-reset-error" role="alert">
      {error}
    </p>
  );

  const backLink = (
    <p className="treatment-auth__footnote">
      <button
        type="button"
        className="treatment-auth__link"
        onClick={onBack}
        disabled={pending}
      >
        {COPY.backToLogin}
      </button>
    </p>
  );

  if (stage === STAGE.code) {
    return (
      <form className="treatment-auth" onSubmit={handleVerify} noValidate>
        <p className="treatment-auth__eyebrow">{COPY.eyebrow}</p>
        <h1 className="treatment-auth__title">{COPY.codeStep.title}</h1>
        <p className="treatment-auth__description">
          {COPY.codeStep.descriptionPrefix} <strong>{email}</strong>.{" "}
          {COPY.codeStep.descriptionSuffix}
        </p>

        <label className="treatment-auth__label" htmlFor="intake-reset-code">
          {COPY.codeStep.codeLabel}
        </label>
        <input
          id="intake-reset-code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          className={`treatment-auth__input${error ? " treatment-auth__input--error" : ""}`}
          placeholder={COPY.codeStep.codePlaceholder}
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "intake-reset-error" : undefined}
          disabled={pending}
        />
        {errorMessage}
        {notice && <p className="treatment-auth__success" role="status">{notice}</p>}

        <button type="submit" className="treatment-auth__submit" disabled={pending}>
          {pending ? COPY.codeStep.submitting : COPY.codeStep.submit}
        </button>

        <p className="treatment-auth__footnote">
          <button
            type="button"
            className="treatment-auth__link treatment-auth__link--strong"
            onClick={() => sendCode(true)}
            disabled={pending || resendIn > 0}
          >
            {COPY.codeStep.resend}
            {resendIn > 0 ? ` (${resendIn}s)` : ""}
          </button>
        </p>
        {backLink}
      </form>
    );
  }

  if (stage === STAGE.password) {
    return (
      <form className="treatment-auth" onSubmit={handleConfirm} noValidate>
        <p className="treatment-auth__eyebrow">{COPY.eyebrow}</p>
        <h1 className="treatment-auth__title">{COPY.passwordStep.title}</h1>

        <label className="treatment-auth__label" htmlFor="intake-new-password">
          {COPY.passwordStep.newPasswordLabel}
          <span className="treatment-auth__required" aria-hidden="true">*</span>
        </label>
        <input
          id="intake-new-password"
          name="new-password"
          type="password"
          autoComplete="new-password"
          className={`treatment-auth__input${error ? " treatment-auth__input--error" : ""}`}
          placeholder={COPY.passwordStep.newPasswordPlaceholder}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          maxLength={MAXIMUM_PASSWORD_LENGTH}
          disabled={pending}
        />

        <label className="treatment-auth__label" htmlFor="intake-confirm-password">
          {COPY.passwordStep.confirmPasswordLabel}
          <span className="treatment-auth__required" aria-hidden="true">*</span>
        </label>
        <input
          id="intake-confirm-password"
          name="confirm-password"
          type="password"
          autoComplete="new-password"
          className={`treatment-auth__input${error ? " treatment-auth__input--error" : ""}`}
          placeholder={COPY.passwordStep.confirmPasswordPlaceholder}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "intake-reset-error" : undefined}
          maxLength={MAXIMUM_PASSWORD_LENGTH}
          disabled={pending}
        />
        {errorMessage}

        <button type="submit" className="treatment-auth__submit" disabled={pending}>
          {pending ? COPY.passwordStep.submitting : COPY.passwordStep.submit}
        </button>
        {backLink}
      </form>
    );
  }

  return (
    <form className="treatment-auth" onSubmit={handleRequest} noValidate>
      <p className="treatment-auth__eyebrow">{COPY.eyebrow}</p>
      <h1 className="treatment-auth__title">{COPY.requestStep.title}</h1>
      <p className="treatment-auth__description">{COPY.requestStep.description}</p>

      <label className="treatment-auth__label" htmlFor="intake-reset-email">
        {COPY.requestStep.emailLabel}
      </label>
      <input
        id="intake-reset-email"
        type="email"
        className="treatment-auth__input treatment-auth__input--readonly"
        value={email}
        readOnly
        autoComplete="username"
      />
      {errorMessage}

      <button type="submit" className="treatment-auth__submit" disabled={pending}>
        {pending ? COPY.requestStep.submitting : COPY.requestStep.submit}
      </button>
      {backLink}
    </form>
  );
}

export default PasswordResetStep;
